import { DesktopDatePicker } from '@mui/x-date-pickers';
import { format, set } from 'date-fns';
import React, { forwardRef, memo, useCallback, useRef, useState } from 'react';

import { DATE_FORMAT } from '~/constants/dateFormats';
import { useUpdateEffect } from '~/hooks/useUpdateEffect';

interface Props {
  label: string;
  value: Date | null;
  error?: boolean;
  disabled?: boolean;
  helperText?: React.ReactNode;
  emptyDatePlaceholder?: string;
  onChange?: (date: Date | null) => void;
  onBlur?: () => void;
}

const Component = forwardRef<HTMLDivElement, Props>(
  ({ label, error, disabled, helperText, emptyDatePlaceholder, value, onChange, onBlur }, ref) => {
    const textFieldRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const [internalValue, setInternalValue] = useState(value);

    useUpdateEffect(() => {
      setInternalValue(value);
    }, [value]);

    const handleAccept = useCallback(
      (value: Date | null) => {
        if (value instanceof Date) {
          onChange?.(set(value, { hours: 17, minutes: 0, seconds: 0, milliseconds: 0 }));
        } else {
          onChange?.(value);
        }
      },
      [onChange],
    );

    const handleClose = useCallback(() => {
      setOpen(false);

      setInternalValue(value);

      setTimeout(() => {
        onBlur?.();

        textFieldRef.current?.blur();
      }, 100);
    }, [onBlur, value]);

    return (
      <DesktopDatePicker
        closeOnSelect={false}
        disabled={disabled}
        label={label}
        localeText={{ okButtonLabel: 'Confirm', cancelButtonLabel: 'Cancel' }}
        open={open}
        ref={ref}
        slotProps={{
          textField: {
            inputRef: textFieldRef,
            error,
            helperText,
            variant: 'outlined',
            placeholder: '',
            InputProps: {
              value: value ? format(value, DATE_FORMAT) : emptyDatePlaceholder || '',
            },
            onClick: () => {
              if (!disabled) {
                setOpen(true);
              }
            },
          },
          field: { readOnly: true },
          actionBar: { actions: ['cancel', 'accept'] },
        }}
        value={internalValue}
        onAccept={handleAccept}
        onChange={setInternalValue}
        onClose={handleClose}
      />
    );
  },
);

export const DateField = memo(Component);
